import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, Archive, Plus, RotateCcw } from "lucide-react"
import { useRef, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { KpiConceptos } from "@/componentes/kpi/KpiNomina"
import { monto } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"
import { Modal } from "@/componentes/ui/Modal"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { tienePermiso } from "@/lib/autorizacion"
import { paraElServidor } from "@/lib/decimales"
import { usarSesion } from "@/lib/sesion"
import { Campo } from "@/paginas/interno/Gastos"
import type { Concepto, FormaCalculo, TipoConcepto } from "@/paginas/interno/nomina/tipos"

/**
 * El catálogo de bonificaciones y descuentos.
 *
 * ⚠ **Un concepto se ARCHIVA, nunca se borra.** Los recibos emitidos lo citan, y
 * borrarlo dejaría una línea explicando un descuento que ya no se puede nombrar.
 * Archivado deja de aplicarse en los cálculos nuevos y sigue explicando los viejos.
 *
 * ⚠ **El porcentaje va sobre el SUELDO BASE**, no sobre el devengado: sobre el
 * devengado, dos conceptos porcentuales se afectarían el uno al otro y el resultado
 * dependería del orden en que se aplicaran.
 */
export function Conceptos() {
  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()
  const [creando, setCreando] = useState(false)
  const [verArchivados, setVerArchivados] = useState(false)
  const puedeAdministrar = tienePermiso(usuario, "nomina:administrar")
  const vista = useRef<HTMLDivElement | null>(null)

  const conceptos = useQuery({
    queryKey: ["nomina-conceptos", verArchivados],
    queryFn: () =>
      api.get<Concepto[]>(`/api/v1/nomina/conceptos?incluir_archivados=${verArchivados}`),
  })

  const crear = useMutation({
    mutationFn: (cuerpo: Record<string, unknown>) =>
      api.post<Concepto>("/api/v1/nomina/conceptos", cuerpo),
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["nomina-conceptos"] })
      setCreando(false)
    },
  })

  const archivar = useMutation({
    mutationFn: ({ id, activo }: { id: number; activo: boolean }) =>
      api.post(`/api/v1/nomina/conceptos/${id}/archivar?activo=${activo}`, {}),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: ["nomina-conceptos"] }),
  })

  if (conceptos.isPending) return <p className="py-6">Cargando…</p>
  if (conceptos.isError) {
    return (
      <p className="flex items-center gap-2 py-6" style={{ color: "var(--destructive)" }}>
        <AlertCircle size={16} /> No se pudieron cargar los conceptos.
      </p>
    )
  }

  const filas = conceptos.data

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={verArchivados}
            onChange={(e) => setVerArchivados(e.target.checked)}
          />
          Ver también los archivados
        </label>
        <div className="flex flex-wrap items-start gap-2">
          <Exportar
            titulo="Conceptos de nómina"
            subtitulo={verArchivados ? "Activos y archivados" : "Sólo los activos"}
            contenido={vista}
          />
          {puedeAdministrar && (
            <Boton variante="primario" onClick={() => setCreando(true)}>
              <Plus size={16} /> Nuevo concepto
            </Boton>
          )}
        </div>
      </div>
      <KpiConceptos />

      <div ref={vista}>
        <Tabla etiqueta="Conceptos de nómina">
          <Cabecera>
            <Th>Código</Th>
            <Th>Nombre</Th>
            <Th>Qué hace</Th>
            <Th derecha>Valor</Th>
            <Th />
          </Cabecera>
          <Cuerpo>
            {filas.length === 0 && (
              <TablaVacia colSpan={5}>
                Todavía no hay conceptos. Sin ellos, el recibo es el sueldo y nada más.
              </TablaVacia>
            )}
            {filas.map((c) => (
              <Fila key={c.id} atenuada={!c.activo}>
                <Td>{c.codigo}</Td>
                <Td>{c.nombre}</Td>
                <Td>
                  <Insignia tono={c.tipo === "DEDUCCION" ? "aviso" : "info"}>
                    {c.tipo === "DEDUCCION" ? "Descuenta" : "Suma"}
                  </Insignia>
                </Td>
                <Td
                  derecha
                  numero
                  data-exportar-valor={
                    c.forma_calculo === "PORCENTAJE_SUELDO"
                      ? `${c.valor.replace(".", ",")} % del sueldo`
                      : monto(c.valor)
                  }
                >
                  {c.forma_calculo === "PORCENTAJE_SUELDO" ? `${c.valor} % del sueldo` : c.valor}
                </Td>
                <Td>
                  {puedeAdministrar && (
                    <button
                      type="button"
                      aria-label={`${c.activo ? "Archivar" : "Reactivar"} ${c.nombre}`}
                      style={{ color: "var(--fg-muted)" }}
                      onClick={() => archivar.mutate({ id: c.id, activo: !c.activo })}
                    >
                      {c.activo ? <Archive size={14} /> : <RotateCcw size={14} />}
                    </button>
                  )}
                </Td>
              </Fila>
            ))}
          </Cuerpo>
        </Tabla>
      </div>

      <Modal
        abierto={creando}
        titulo="Nuevo concepto"
        descripcion="Una bonificación que suma, o un descuento que resta."
        onCerrar={() => setCreando(false)}
      >
        {creando && (
          <FormularioConcepto
            enviando={crear.isPending}
            error={crear.error instanceof ErrorApi ? crear.error.message : null}
            onEnviar={(cuerpo) => crear.mutate(cuerpo)}
            onCancelar={() => setCreando(false)}
          />
        )}
      </Modal>
    </section>
  )
}

function FormularioConcepto({
  enviando,
  error,
  onEnviar,
  onCancelar,
}: {
  enviando: boolean
  error: string | null
  onEnviar: (cuerpo: Record<string, unknown>) => void
  onCancelar: () => void
}) {
  const [forma, setForma] = useState<FormaCalculo>("MONTO_FIJO")
  const [tipo, setTipo] = useState<TipoConcepto>("ASIGNACION")

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault()
        const datos = new FormData(e.currentTarget)
        const texto = (campo: string) => String(datos.get(campo) ?? "").trim()
        onEnviar({
          codigo: texto("codigo"),
          nombre: texto("nombre"),
          tipo,
          forma_calculo: forma,
          valor: paraElServidor(texto("valor") || "0"),
          descripcion: texto("descripcion") || null,
        })
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Campo para="cp-codigo" etiqueta="Código" ayuda="Corto y en mayúsculas: BONO-TRANS.">
          <input id="cp-codigo" name="codigo" required className="campo mt-1" />
        </Campo>
        <Campo para="cp-nombre" etiqueta="Nombre">
          <input id="cp-nombre" name="nombre" required className="campo mt-1" />
        </Campo>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Campo para="cp-tipo" etiqueta="Qué hace">
          <select
            id="cp-tipo"
            value={tipo}
            onChange={(e) => setTipo(e.target.value as TipoConcepto)}
            className="campo mt-1"
          >
            <option value="ASIGNACION">Suma al sueldo</option>
            <option value="DEDUCCION">Descuenta del sueldo</option>
          </select>
        </Campo>
        <Campo para="cp-forma" etiqueta="Cómo se calcula">
          <select
            id="cp-forma"
            value={forma}
            onChange={(e) => setForma(e.target.value as FormaCalculo)}
            className="campo mt-1"
          >
            <option value="MONTO_FIJO">Monto fijo</option>
            <option value="PORCENTAJE_SUELDO">Porcentaje del sueldo base</option>
          </select>
        </Campo>
      </div>

      <Campo
        para="cp-valor"
        etiqueta={forma === "PORCENTAJE_SUELDO" ? "Porcentaje" : "Monto"}
        ayuda={
          forma === "PORCENTAJE_SUELDO"
            ? "Sobre el sueldo base, nunca sobre el devengado."
            : "Es el valor por omisión: en el contrato de cada quien se puede cambiar."
        }
      >
        <input
          id="cp-valor"
          name="valor"
          inputMode="decimal"
          defaultValue="0"
          className="campo mt-1 tabular-nums"
        />
      </Campo>

      <Campo para="cp-desc" etiqueta="Descripción" ayuda="Opcional.">
        <input id="cp-desc" name="descripcion" className="campo mt-1" />
      </Campo>

      {error && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          {error}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <Boton variante="fantasma" onClick={onCancelar}>
          Cancelar
        </Boton>
        <Boton type="submit" variante="primario" disabled={enviando}>
          Crear concepto
        </Boton>
      </div>
    </form>
  )
}
